/* eslint-disable max-len */
import { capitalCase } from 'capital-case';
import React from 'react';
import SplitPane from 'react-split-pane';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';
import { Link, Redirect } from 'wouter';
import { format } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { AccountRequestInfo } from '../../api/endpoints/admin';
import { Icon } from '../../concepts/resources/Icon';
import { useLoader, useSubmitter } from '../../platform/Hook';
import { Fill } from '../../ui/Fill';
import { InfoView } from '../../ui/InfoView';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { Table } from '../../ui/Table';
import { VerticalTabSet } from '../../ui/VerticalTabSet';
import { UseLocationScope } from '../AppLocation';
import { AppNavigator } from '../AppNavigator';
import { useResolve } from '../Container';
import { AccountHeaderBlock } from '../account/AccountHeaderBlock';
import { AccountModel } from '../account/AccountModel';
import { PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { Crumbs } from '../common/Crumbs';
import { Page } from '../common/Page';
import { ButtonStrip, Copy, Em, FlexFrame, Spacer } from '../dashboard/elements/LayoutElements';
import { Button, Colors, remInPx } from './../../ui';

const XSplitPane = SplitPane as any;

const itemHeading = (item: AccountRequestInfo) => {
    if (item?.type === 'account-join') return `Access Request from ${item.creator.name}`;
    if (item?.type === 'account-invite') return `Invitation Request from ${item.creator.name}`;
    return '';
};

const itemSummary = (item: AccountRequestInfo, account: AccountModel) => {
    if (item?.type === 'account-join') {
        return <>
            <Em>{item?.creator?.name}</Em> would like to join {account.label}.
            Your organizations <Link to="~/manage/security">access control settings</Link> require this be approved by an administrator.
        </>;
    }
    if (item?.type === 'account-invite') {
        return <>
            <Em>{item?.creator?.name}</Em> would like to invite <Em>{item?.params?.target}</Em> to {account.label}.
            Your organizations <Link to="~/manage/security">access control settings</Link> require this invitation be approved by an administrator.
        </>;
    }
    return '';
};

const typeLabels = {
    'account-join': 'Access Review',
    'account-invite': 'Invitation Review',
};

const stateColors = {
    pending: Colors.primary,
    approved: Colors.positive,
    denied: Colors.negative,
    expired: Colors.gray300,
};

const { Section, LargeHeading, Heading, DetailTable, DetailRow, DetailCell } = InfoView;

const ActionBadge = styled.div<{ color }>`
    display: inline-block;
    position: relative;
    padding: .2rem .4rem;
    border-radius: .3rem;
    color: white;
    font-weight: 450;
    text-transform: uppercase;
    font-size: .875rem;
    background: ${p => p.color};
`;

const Email = styled.div`
    font-size: .875rem;
    opacity: .65;
`;

const RequestListFrame = styled.div`
    
    ${Table} {
        border-left: none;
        border-right: none;

        & td:first-child {
            padding-left: 2rem;
        }

        & td:last-child {
            padding-right: 2rem;
        }
    }
`;

const OpenRequestList = React.memo(() => {

    const api = useResolve(ApiClient);
    const nav = useResolve(AppNavigator);

    const [loading, items] = useLoader(async () => await api.exec(v2.admin.requests.list(true)), []);
    const goto = (id: string) => nav.go(`~/manage/approvals/open:${id}`);

    return <LoadingGuard loading={loading}>
        <Section>
            <LargeHeading>
                Open Requests
            </LargeHeading>
            <Copy>These are the outstanding requests from people within your team.</Copy>
        </Section>
        <RequestListFrame>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Cell>Status</Table.Cell>
                        <Table.Cell>Type</Table.Cell>
                        <Table.Cell>Created By</Table.Cell>
                        <Table.Cell>Created On</Table.Cell>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {items && items.map(x =>
                        <Table.Row key={x.id} clickable onClick={() => goto(x.id)}>
                            <Table.Cell><ActionBadge color={stateColors[x.state]}>{x.state}</ActionBadge></Table.Cell>
                            <Table.Cell>{typeLabels[x.type] ?? ''}</Table.Cell>
                            <Table.Cell>{x.creator?.name} <Email>{x.creator?.email}</Email></Table.Cell>
                            <Table.Cell>{format.datetime(x.createdAt)}</Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
        </RequestListFrame>
    </LoadingGuard>;
});

const ClosedRequestList = React.memo(() => {

    const api = useResolve(ApiClient);
    const nav = useResolve(AppNavigator);

    const [loading, items] = useLoader(async () => await api.exec(v2.admin.requests.list(false)), []);
    const goto = (id: string) => nav.go(`~/manage/approvals/closed:${id}`);

    return <LoadingGuard loading={loading}>
        <Section>
            <LargeHeading>
                Closed Requests
            </LargeHeading>
            <Copy>This is the request archive for your organization.</Copy>
        </Section>
        <RequestListFrame>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Cell>Status</Table.Cell>
                        <Table.Cell>Type</Table.Cell>
                        <Table.Cell>Created By</Table.Cell>
                        <Table.Cell>Created On</Table.Cell>
                        <Table.Cell>Actioned By</Table.Cell>
                        <Table.Cell>Actioned On</Table.Cell>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {items && items.map(x =>
                        <Table.Row key={x.id} clickable onClick={() => goto(x.id)}>
                            <Table.Cell><ActionBadge color={stateColors[x.state]}>{x.state}</ActionBadge></Table.Cell>
                            <Table.Cell>{typeLabels[x.type] ?? ''}</Table.Cell>
                            <Table.Cell>{x.creator?.name} <Email>{x.creator?.email}</Email></Table.Cell>
                            <Table.Cell>{format.datetime(x.createdAt)}</Table.Cell>
                            <Table.Cell>{x.resolver?.name} <Email>{x.resolver?.email}</Email></Table.Cell>
                            <Table.Cell>{format.datetime(x.resolvedAt)}</Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
        </RequestListFrame>
    </LoadingGuard>;
});

const RequestItem = React.memo<{ id: string }>(({ id }) => {

    const api = useResolve(ApiClient);
    const account = useResolve(AccountModel);
    const nav = useResolve(AppNavigator);

    const [loading, item] = useLoader(async () => await api.exec(v2.admin.requests.info(id)), []);
    const [onAction, working, result] = useSubmitter(async (value: 'approve' | 'deny') => {
        await api.exec(v2.admin.requests.action(item.id, value));
        nav.go('/manage/approvals/open');
    }, [item]);

    return <LoadingGuard loading={loading}>
        <InfoView>
            <Section>
                <LargeHeading>{itemHeading(item)}</LargeHeading>
                <Heading>
                    Information:
                </Heading>
                <DetailTable>
                    <DetailRow>
                        <DetailCell shrink>Id:</DetailCell>
                        <DetailCell>{item?.id}</DetailCell>
                    </DetailRow>
                    <DetailRow>
                        <DetailCell shrink>Type:</DetailCell>
                        <DetailCell>{typeLabels[item?.type] ?? ''}</DetailCell>
                    </DetailRow>
                    <DetailRow>
                        <DetailCell shrink>Status:</DetailCell>
                        <DetailCell>
                            {item && <ActionBadge color={stateColors[item.state]}>{item.state}</ActionBadge>}
                        </DetailCell>
                    </DetailRow>
                    <DetailRow>
                        <DetailCell shrink>Opened At:</DetailCell>
                        <DetailCell>{format.datetime(item?.createdAt)} ({format.since(item?.createdAt)})</DetailCell>
                    </DetailRow>
                    <DetailRow>
                        <DetailCell shrink>Opened By:</DetailCell>
                        <DetailCell>{item?.creator?.name} &lt;{item?.creator?.email}&gt;</DetailCell>
                    </DetailRow>
                    {item && item.state !== 'pending' && <>
                        {Object.keys(item.params).map(k =>
                            <DetailRow key={k}>
                                <DetailCell shrink>{capitalCase(k)}:</DetailCell>
                                <DetailCell>{item.params[k]}</DetailCell>
                            </DetailRow>
                        )}
                        <DetailRow>
                            <DetailCell shrink>Actioned At:</DetailCell>
                            <DetailCell>{format.datetime(item?.resolvedAt)} ({format.since(item.resolvedAt)})</DetailCell>
                        </DetailRow>
                        <DetailRow>
                            <DetailCell shrink>Actioned By:</DetailCell>
                            <DetailCell>{item?.resolver?.name} &lt;{item?.resolver?.email}&gt;</DetailCell>
                        </DetailRow>
                    </>}
                </DetailTable>
            </Section>
            {item && item.state === 'pending' && <Section>
                <Heading>
                    Summary:
                </Heading>
                <Copy>
                    {itemSummary(item, account)}
                </Copy>
            </Section>}
            {item && item.state === 'pending' && <Section>
                <Heading>
                    Available Actions:
                </Heading>
                <ButtonStrip>
                    <Button disabled={working} size="lg" variant="positive" label="Approve" onAction={() => onAction('approve')} />
                    <Button disabled={working} size="lg" variant="negative" label="Deny" onAction={() => onAction('deny')} />
                </ButtonStrip>
                <Spacer f={2} />
                <ResultInfoForSubmitter result={result} />
            </Section>}
        </InfoView>
    </LoadingGuard>;
});

const ManageApprovalsLayout: LFC<{ param?: string }> = ({ param }) => {

    const [leftSize, setLeftSize] = useLocalStorage('deqo/ui/approvals/leftSize', remInPx(20));

    if (!param) {
        return <Redirect to={'~/manage/approvals/open'} />;
    }

    const [mode, ref] = param?.split(':');

    return <Fill flex="column">
        <FlexFrame>
            <VerticalTabSet>
                <XSplitPane className="flex" split="vertical" minSize={remInPx(12)} size={leftSize} maxSize={remInPx(30)} onChange={setLeftSize}>
                    <VerticalTabSet.Menu expand>
                        <VerticalTabSet.Link to="~/manage/approvals/open" selected={param.startsWith('open')}>
                            <Icon size="1.5" src="deqo://icons/hero/book-open" />&nbsp;&nbsp;Open Requests
                        </VerticalTabSet.Link>
                        <VerticalTabSet.Link to="~/manage/approvals/closed" selected={param.startsWith('closed')}>
                            <Icon size="1.5" src="deqo://icons/hero/check" />&nbsp;&nbsp;Closed Requests
                        </VerticalTabSet.Link>
                    </VerticalTabSet.Menu>
                    <VerticalTabSet.Content>
                        {!ref && mode === 'open' && <OpenRequestList />}
                        {!ref && mode === 'closed' && <ClosedRequestList />}
                        {!!ref && <RequestItem id={ref} />}
                    </VerticalTabSet.Content>
                </XSplitPane>
            </VerticalTabSet>
        </FlexFrame>
    </Fill>;
};

export const ManageApprovalsView: LFC<{ param?: string }> = (props) => {
    return <>
        <UseLocationScope name="approvals" label="Approvals" />
        <Page.Header>
            <Crumbs items={[
                ['Manage', '/manage'],
                ['Approvals', null],
            ]} />
            <Page.HeaderPush>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page.HeaderPush>
        </Page.Header>
        <Page.Content>
            <ManageApprovalsLayout {...props} />
        </Page.Content>
    </>;
};

